import { Box, Button, Grid, Text } from "@chakra-ui/react";
import { InputBoxSearch } from "components/input-box-search";
import { useState } from "react";
import { makeEvent, useEventListener } from "services/events";
import { loadEmpresas, validateTransferirSaldo } from "./utils";
import { isUserGerente } from "utils/is-gerente";
import { useApplicationContext } from "contexts/ApplicationContext";
import { DropdownOptionProps } from "components/dropdown";
import { CustomModal } from "components/custom-modal";
import apiCarteira from "api/api-carteira";
import { Toast } from "components/toast";
import { DolarIcon } from "components/vectors/dolar-icon";
import { InputField } from "components/input-field";
import { DropdownField } from "components/dropdown-field";
import { CloseIcon } from "components/vectors/close-icon";
import { currencyMaskFormat } from "utils/string-formats";
import { updateWallet } from "../wallet-table";

export interface TransferirSaldoParams {
  customerIdDebito?: number;
  customerIdCredito?: number;
  valor?: number;
  tipoCarteiraDebito?: "GERAL" | "FGTS";
  tipoCarteiraCredito?: "GERAL" | "FGTS";
}

export function ModalTransferirSaldo() {
  const [isOpen, setIsOpen] = useState(false);
  const [isTransfering, setIsTransfering] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [params, setParams] = useState<TransferirSaldoParams>({});
  const [empresas, setEmpresas] = useState<DropdownOptionProps[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const { user } = useApplicationContext();
  const isGerente = isUserGerente(user);

  const onOpen = () => {
    setIsOpen(true);
    setParams((prev) => ({
      ...prev,
      customerIdDebito: user.userData.customerId,
    }));
    loadEmpresas({ setEmpresas, setLoadingEmpresas, user });
  };

  const onClose = () => {
    setIsOpen(false);
    setParams({});
    setErrors({});
    setEmpresas([]);
  };

  const onChangeFields = (field: keyof TransferirSaldoParams, value: any) => {
    params[field] = value;
    setParams((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      delete prev[field];
      return { ...prev };
    });
  };

  const handleSubmit = async () => {
    const { isValid, errors, body } = validateTransferirSaldo(params);
    // if (!isValid) {
    //   console.log("Errors => ", errors);
    //   return setErrors(errors);
    // }

    setIsTransfering(true);
    try {
      await apiCarteira.post(`/carteiras/transferir`, body);
      Toast({
        title: "Transferência realizada com sucesso",
        status: "success",
      });
      onClose();
      updateWallet();
    } catch (e: any) {
      const errorMessage = e?.response?.data?.msgInterface;
      Toast({
        title: "Erro ao transferir saldo",
        status: "error",
        description: errorMessage,
        duration: 5500,
      });
    } finally {
      setIsTransfering(false);
    }
  };

  useEventListener("openModalTransferirSaldo", onOpen);

  const modalFooter = (
    <>
      <Button
        onClick={() => handleSubmit()}
        leftIcon={<DolarIcon width="16px" height="16px" />}
        loadingText="Transferindo"
        isLoading={isTransfering}
      >
        Transferir
      </Button>
      <Button onClick={onClose} leftIcon={<CloseIcon />} variant="outline">
        Cancelar
      </Button>
    </>
  );

  return (
    <CustomModal
      size="3xl"
      modalTitle="Transferir Saldo"
      isOpen={isOpen}
      onClose={onClose}
      modalFooter={modalFooter}
    >
      <Grid templateColumns={"repeat(4, 1fr)"} gap="16px">
        <Box gridArea="auto / span 3">
          <Text mb="8px" fontWeight="medium">
            De Empresa
          </Text>
          <InputBoxSearch
            options={empresas}
            onChange={(value) => onChangeFields("customerIdDebito", value)}
            checkIsInclude={(option) => {
              return params.customerIdDebito === option.value;
            }}
            isLoading={loadingEmpresas}
            value={
              empresas.find(
                (customer) => params.customerIdDebito === customer.value
              )?.name
            }
            inputBoxProps={{
              bg: "#fff",
              onFocus: (e) => {
                e.currentTarget.textContent = "";
              },
              onBlur: (e) => {
                e.currentTarget.textContent =
                  empresas.find(
                    (customer) => params.customerIdDebito === customer.value
                  )?.name || "";
              },
            }}
            openOnFocus
            closeOnSelect
            errorMessage={errors["customerIdDebito"]}
          />
        </Box>
        <Box>
          <DropdownField
            title="Tipo carteira débito"
            onChange={(value) => onChangeFields("tipoCarteiraDebito", value)}
            value={params.tipoCarteiraDebito}
            options={[
              { name: "Geral", value: "GERAL" },
              { name: "FGTS", value: "FGTS" },
            ]}
            errorMessage={errors["tipoCarteiraDebito"]}
          />
        </Box>
        <Box gridArea="auto / span 3">
          <Text mb="8px" fontWeight="medium">
            Para Empresa
          </Text>
          <InputBoxSearch
            options={empresas}
            onChange={(value) => onChangeFields("customerIdCredito", value)}
            checkIsInclude={(option) => {
              return params.customerIdCredito === option.value;
            }}
            isLoading={loadingEmpresas}
            value={
              empresas.find(
                (customer) => params.customerIdCredito === customer.value
              )?.name
            }
            inputBoxProps={{
              bg: "#fff",
              onFocus: (e) => {
                e.currentTarget.textContent = "";
              },
              onBlur: (e) => {
                e.currentTarget.textContent =
                  empresas.find(
                    (customer) => params.customerIdCredito === customer.value
                  )?.name || "";
              },
            }}
            openOnFocus
            closeOnSelect
            errorMessage={errors["customerIdCredito"]}
          />
        </Box>

        <Box>
          <DropdownField
            title="Tipo carteira crédito"
            onChange={(value) => onChangeFields("tipoCarteiraCredito", value)}
            value={params.tipoCarteiraCredito}
            options={[
              { name: "Geral", value: "GERAL" },
              { name: "FGTS", value: "FGTS" },
            ]}
            errorMessage={errors["tipoCarteiraCredito"]}
          />
        </Box>
        <Box gridArea="auto / span 3">
          <InputField
            title="Valor"
            onChange={(e) =>
              onChangeFields("valor", currencyMaskFormat(e.target.value).raw)
            }
            value={currencyMaskFormat(params.valor).value}
            errorMessage={errors["valor"]}
          />
        </Box>
      </Grid>
    </CustomModal>
  );
}
export function openModalTransferirSaldo() {
  makeEvent("openModalTransferirSaldo");
}
